import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  FolderOutlined
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { addAuthHeader } from '../../../utils/authUtils';

// Components
import DocumentSummaryTiles from './components/DocumentSummaryTiles';
import DocumentList from './components/DocumentList';
import AddDocumentDialog from './components/AddDocumentDialog';
import DocumentDetailDialog from './components/DocumentDetailDialog';

/**
 * Compute document status from expiryDate
 */
const computeStatus = (expiryDate) => {
  if (!expiryDate) return 'ACTIVE';
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'EXPIRED';
  if (diffDays <= 30) return 'EXPIRING_SOON';
  return 'ACTIVE';
};

/**
 * Compute days until expiry
 */
const computeDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Format file size
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Get organization ID from auth info
 */
const getOrganizationId = () => {
  try {
    const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
    return authInfo?.principalInfo?.organisationId || null;
  } catch {
    return null;
  }
};

/**
 * Transform API groups into flat document rows for the table.
 *
 * The API returns one group per documentGroupId (version batch).
 * Multiple files can exist within a single group (e.g. multi-page upload).
 *
 * Display logic (mirrors employee document page):
 * - For each document type, find the *current* group (isCurrent === true).
 *   If none is current, fall back to the group with the highest version.
 * - Show every file in that current group as its own table row.
 * - Attach all groups for the same document type so the detail dialog
 *   can render full version history.
 */
const transformGroupsToRows = (groups) => {
  if (!groups) return [];

  // 1. Bucket groups by documentTypeId
  const byType = {};
  groups.forEach((group) => {
    const typeId = group.common?.documentTypeId;
    if (!typeId) return;
    if (!byType[typeId]) byType[typeId] = [];
    byType[typeId].push(group);
  });

  const rows = [];

  // 2. For each document type pick the current group, then emit one row per file
  Object.values(byType).forEach((typeGroups) => {
    // Find the current group; fall back to highest-version group
    let currentGroup = typeGroups.find((g) => g.common?.isCurrent === true);
    if (!currentGroup) {
      currentGroup = typeGroups.reduce((best, g) => {
        const bestVersion = best.documents?.[0]?.version ?? 0;
        const gVersion = g.documents?.[0]?.version ?? 0;
        return gVersion > bestVersion ? g : best;
      }, typeGroups[0]);
    }

    const { common, documents, documentGroupId } = currentGroup;
    if (!documents || documents.length === 0) return;

    const status = computeStatus(common.expiryDate);
    const daysUntilExpiry = computeDaysUntilExpiry(common.expiryDate);

    // Collect ALL documents across ALL groups of this type (for version history)
    const allDocsForType = typeGroups.flatMap((g) =>
      (g.documents || []).map((d) => ({
        ...d,
        documentGroupId: g.documentGroupId,
        isCurrent: g.common?.isCurrent,
      }))
    );

    // 3. One row per document type — primary doc shown in table,
    //    all files in current group available in detail dialog
    const primaryDoc = documents.find(d => d.isPrimary) || documents[0];

    rows.push({
      id: primaryDoc.id,
      documentGroupId,
      documentTypeId: common.documentTypeId,
      documentTypeName: common.documentTypeName,
      documentTypeCode: common.documentTypeCode,
      categoryId: common.categoryId,
      categoryName: common.categoryName || 'Uncategorized',
      categoryCode: common.categoryCode || 'UNCATEGORIZED',
      issuingAuthority: common.issuingAuthority,
      issuingAuthorityName: common.issuingAuthority,
      issuingAuthorityShortName: common.issuingAuthority,
      issueDate: common.issueDate,
      expiryDate: common.expiryDate,
      documentNumber: common.documentNumber,
      isCurrent: common.isCurrent,
      status,
      daysUntilExpiry,
      // File info from primary doc (shown in the table row)
      filePath: primaryDoc.filePath,
      fileName: primaryDoc.fileName,
      contentType: primaryDoc.contentType,
      fileType: primaryDoc.contentType,
      fileSize: primaryDoc.fileSize,
      fileSizeFormatted: formatFileSize(primaryDoc.fileSize),
      version: primaryDoc.version,
      isPrimary: primaryDoc.isPrimary,
      active: primaryDoc.active,
      uploadedAt: primaryDoc.uploadDate,
      uploadedByName: primaryDoc.uploadedByName || '-',
      ticketId: primaryDoc.ticketId,
      notes: primaryDoc.metadata,
      // All files in the current version group (for detail dialog)
      currentGroupDocs: documents,
      // All documents across all versions for this type (for history dialog)
      allVersionDocs: allDocsForType,
      // File count in current group
      fileCount: documents.length,
    });
  });

  return rows;
};

/**
 * Compute summary from document rows (one row per document type).
 */
const computeSummary = (rows) => {
  let active = 0, expiringSoon = 0, expired = 0;
  rows.forEach(row => {
    switch (row.status) {
      case 'ACTIVE': active++; break;
      case 'EXPIRING_SOON': expiringSoon++; break;
      case 'EXPIRED': expired++; break;
      default: break;
    }
  });
  return { total: rows.length, active, expiringSoon, expired };
};

/**
 * Organization Documents Page
 * Main page for managing organization documents
 */
const OrganizationDocumentsPage = () => {
  const dispatch = useDispatch();

  // State
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [apiGroups, setApiGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Set screen title
  useEffect(() => {
    dispatch(updateScreenTitle('Organization Documents'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  const organizationId = getOrganizationId();

  // Load data from API
  const loadData = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        API_ENDPOINTS.ORGANIZATION_DOCUMENTS.GROUPED(organizationId),
        { headers: addAuthHeader() }
      );
      const data = response.data;
      setApiGroups(data.groups || []);
      const rows = transformGroupsToRows(data.groups);
      setDocuments(rows);
      setSummary(computeSummary(rows));
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
      setSummary({ total: 0, active: 0, expiringSoon: 0, expired: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Build category tabs from distinct categories in documents
  const categories = useMemo(() => {
    const catMap = new Map();
    documents.forEach(doc => {
      if (doc.categoryCode && !catMap.has(doc.categoryCode)) {
        catMap.set(doc.categoryCode, {
          id: doc.categoryId || doc.categoryCode,
          code: doc.categoryCode,
          name: doc.categoryName,
        });
      }
    });
    return [
      { id: 'ALL', code: 'ALL', name: 'All Documents' },
      ...Array.from(catMap.values()),
    ];
  }, [documents]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Handle category change
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setSelectedStatusFilter(null);
  };

  // Handle status filter from summary tiles
  const handleStatusFilter = (statusFilter) => {
    setSelectedStatusFilter(statusFilter === selectedStatusFilter ? null : statusFilter);
  };

  // Filter documents by category and status
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'ALL' || doc.categoryCode === selectedCategory;
    let matchesStatus = true;
    if (selectedStatusFilter) {
      switch (selectedStatusFilter) {
        case 'active':
          matchesStatus = doc.status === 'ACTIVE';
          break;
        case 'expiringSoon':
          matchesStatus = doc.status === 'EXPIRING_SOON';
          break;
        case 'expired':
          matchesStatus = doc.status === 'EXPIRED';
          break;
        default:
          matchesStatus = true;
      }
    }
    return matchesCategory && matchesStatus;
  });

  const [detailInitialTab, setDetailInitialTab] = useState(0);

  // Handle document click
  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setDetailInitialTab(0);
    setDetailDialogOpen(true);
  };

  // Handle version history click — opens dialog on history tab
  const handleViewHistory = (document) => {
    setSelectedDocument(document);
    setDetailInitialTab(1);
    setDetailDialogOpen(true);
  };

  // Handle add document
  const handleAddDocument = (category) => {
    if (category && category !== 'ALL') {
      setSelectedCategory(category);
    }
    setAddDialogOpen(true);
  };

  // Handle document saved
  const handleDocumentSaved = () => {
    setAddDialogOpen(false);
    handleRefresh();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FolderOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Organization Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your company's official documents and certificates
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' }
                }}
              >
                {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleAddDocument(selectedCategory)}
              sx={{
                px: 3,
                fontWeight: 'bold',
                boxShadow: 2
              }}
            >
              Add Document
            </Button>
          </Box>
        </Box>

        {/* Summary Tiles */}
        <DocumentSummaryTiles
          summary={summary}
          selectedFilter={selectedStatusFilter}
          onFilterChange={handleStatusFilter}
        />

        {/* Main Content Card */}
        <Card sx={{ mt: 3, overflow: 'hidden' }}>
          {/* Category Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 56
                }
              }}
            >
              {categories.map((category) => {
                const count = category.code === 'ALL'
                  ? documents.length
                  : documents.filter(d => d.categoryCode === category.code).length;

                return (
                  <Tab
                    key={category.code}
                    value={category.code}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {category.name}
                        {count > 0 && (
                          <Chip
                            label={count}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              bgcolor: selectedCategory === category.code ? 'primary.main' : 'grey.300',
                              color: selectedCategory === category.code ? 'white' : 'text.secondary'
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Active Filter Indicator */}
          {selectedStatusFilter && (
            <Box sx={{ px: 3, py: 1.5, bgcolor: 'primary.50', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Filtered by:
                </Typography>
                <Chip
                  label={
                    selectedStatusFilter === 'active' ? 'Active' :
                    selectedStatusFilter === 'expiringSoon' ? 'Expiring Soon' :
                    selectedStatusFilter === 'expired' ? 'Expired' : ''
                  }
                  size="small"
                  onDelete={() => setSelectedStatusFilter(null)}
                  color="primary"
                />
                <Typography variant="body2" color="text.secondary">
                  ({filteredDocuments.length} documents)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Document List */}
          <Box sx={{ p: 3 }}>
            {filteredDocuments.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 8,
                  color: 'text.secondary'
                }}
              >
                <FolderOutlined sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  No documents found
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}>
                  {selectedStatusFilter
                    ? `No documents match the selected filter`
                    : selectedCategory === 'ALL'
                      ? 'Upload your first organization document to get started'
                      : `No documents in ${categories.find(c => c.code === selectedCategory)?.name || selectedCategory}`}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddDocument(selectedCategory)}
                >
                  Add Document
                </Button>
              </Box>
            ) : (
              <DocumentList
                documents={filteredDocuments}
                onDocumentClick={handleDocumentClick}
                onViewHistory={handleViewHistory}
                onRefresh={handleRefresh}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* Add Document Dialog */}
      <AddDocumentDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleDocumentSaved}
        preSelectedCategory={selectedCategory !== 'ALL' ? selectedCategory : null}
        organizationId={organizationId}
      />

      {/* Document Detail Dialog */}
      <DocumentDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onRefresh={handleRefresh}
        initialTab={detailInitialTab}
      />
    </>
  );
};

export default OrganizationDocumentsPage;
